import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck } from '@ng-icons/lucide';
import { BrnSelectItem } from '@spartan-ng/brain/select';
import { classes, hlm } from '@spartan-ng/helm/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ClassValue } from 'clsx';

const selectItemVariants = cva(
  'data-highlighted:bg-accent data-highlighted:text-accent-foreground not-data-[variant=destructive]:data-highlighted:**:text-accent-foreground gap-2.5 text-sm font-medium *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 relative flex w-full cursor-default items-center outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 [&_ng-icon]:pointer-events-none [&_ng-icon]:shrink-0',
  {
    variants: {
      variant: {
        default: 'rounded-2xl py-2 ps-3 pe-8',
        'surface-secondary':
          'rounded-md py-1.5 ps-2 pe-8 aria-selected:bg-[var(--surface-neutral-100)] aria-selected:text-[var(--corporate-text-primary)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export type SelectItemVariants = VariantProps<typeof selectItemVariants>;

@Component({
  selector: 'hlm-select-item',
  imports: [NgIcon],
  providers: [provideIcons({ lucideCheck })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [{ directive: BrnSelectItem, inputs: ['id', 'disabled', 'value'] }],
  host: { 'data-slot': 'select-item' },
  template: `
    <ng-content />
    @if (_active()) {
      <ng-icon
        name="lucideCheck"
        class="absolute end-2 flex items-center justify-center text-[length:--spacing(4)]"
        aria-hidden="true"
      />
    }
  `,
})
export class HlmSelectItem {
  private readonly _brnSelectItem = inject(BrnSelectItem);
  public readonly userClass = input<ClassValue>('', { alias: 'class' });
  public readonly variant = input<SelectItemVariants['variant']>('default');

  protected readonly _active = this._brnSelectItem.active;

  constructor() {
    classes(() => [hlm(selectItemVariants({ variant: this.variant() })), this.userClass()]);
  }
}
